import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
  AppsOutlined,
  CheckCircleRounded,
  Groups2Outlined,
  GroupsRounded,
  NotesOutlined,
  PeopleOutlineRounded,
  SecurityOutlined,
  ToggleOnOutlined,
} from "@mui/icons-material";
import { useFetch } from "services/hooks/useFetch";
import {
  checkEditorPermission,
  checkViewerPermission,
  getAdministrationRedirectPath,
  isProvisioningLoaded,
} from "services/utils/HelperFunction";
import { setGlobalSearchValue } from "services/Redux/userToken";
import sessionHandling from "services/utils/notificationUtils";
import { showToast } from "services/utils/Status";
import { scrollTop } from "services/constants";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designShell,
} from "services/constants/designTokens";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { Loader } from "components/Loader";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { paginationFooter, roleAndGroupUi, statusChip } from "../ui";
import { ChipList, getGroupsForRole, getRoleStatus } from "./roleUiKit";
import { useListToolbar, useMeasuredHeight } from "../useListToolbar";
import { RoleViewPanel } from "./RoleViewPanel";

// Columns for the table and for the shared list toolbar (saved views,
// search, download, sort, filter). The keys, storage key and default view id
// match the previous list so users' existing saved views still apply. Each
// key holds a plain string on the row so CustomFilter can search, sort,
// filter and export it; the table renders a richer cell per key.
const ROLE_HEADERS = [
  { name: "Role Name", key: "role_name", type: "string", icon: <SecurityOutlined fontSize="small" /> },
  { name: "Description", key: "description", type: "string", icon: <NotesOutlined fontSize="small" /> },
  { name: "Modules", key: "products", type: "string", icon: <AppsOutlined fontSize="small" /> },
  { name: "Assigned User Groups", key: "user_groups", type: "string", icon: <Groups2Outlined fontSize="small" /> },
  { name: "Status", key: "status_label", type: "autocomplete", icon: <ToggleOnOutlined fontSize="small" /> },
];
// Relative column weights. Widths are shared out among whichever columns
// the active view shows, so a view with fewer columns still fills the
// table instead of leaving a blank strip on the right.
const COLUMN_WEIGHTS = {
  role_name: 16,
  description: 25,
  products: 23,
  user_groups: 20,
  status_label: 11,
};
// The list's columns before the redesign (key -> old default label), used
// to upgrade views saved back then -- see useListToolbar.
const LEGACY_ROLE_HEADERS = {
  role_name: "Role",
  products: "Modules",
  status_label: "Status",
  "": "Actions",
};
const ROLE_LIST_DEFAULT_VIEW_ID = "__default_role_list_view__";

const pct = (count, total) => (total === 0 ? "0.0" : ((count / total) * 100).toFixed(1));

export const UserRole = ({
  setScreenNavigation,
  setEditRowDetails,
  onOpenUserGroup,
  // { role, key }: set by the parent when a role is clicked in a User
  // Group's "Related User Roles"; opens View Role for it (key makes a
  // repeat click on the same role open it again).
  openRoleRequest = null,
  isEditOpen = false,
  // Bumped by the parent after Edit Role saves: the list refetches in place
  // and an open View Role panel stays open and reloads (2026-10-01, user
  // request -- a save used to remount this list and close the view too).
  editSavedSignal = 0,
  // Tells the parent whether Edit Role should open expanded (it was clicked
  // from an expanded View Role).
  onEditExpandedChange,
  // The mode Edit Role was last in, so View Role comes back the same way.
  editExpanded,
}) => {
  const { domain, token, userDetails, userScreenAccess } = useSelector((state) => state.tokenReducer);
  const dispatch = useDispatch();
  const history = useHistory();

  const canEdit = checkEditorPermission(userScreenAccess?.module_details, "Roles & User Group", "Roles");
  const hasViewAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Roles & User Group",
    "Roles",
  );
  const canViewUserGroups = checkViewerPermission(
    userScreenAccess?.module_details,
    "Roles & User Group",
    "User Group",
  );
  // userScreenAccess starts as last session's cached snapshot until this
  // page's provisioning fetch resolves -- don't redirect or fetch off it.
  const provisioningLoaded = isProvisioningLoaded(userScreenAccess, "Administration");

  // No per-route guard exists, so a refresh on this tab without Roles view
  // access sends the user to a screen they can see instead.
  useEffect(() => {
    if (!provisioningLoaded || hasViewAccess) return;
    history.replace(getAdministrationRedirectPath(userScreenAccess?.module_details));
  }, [provisioningLoaded, hasViewAccess, userScreenAccess, history]);

  // The url is withheld until access is confirmed so a viewer without
  // access never triggers a permission-denied toast.
  const {
    result: roles,
    loading,
    setLoad,
  } = useFetch(provisioningLoaded && hasViewAccess ? `${domain}product-control/roles/summary/` : null);

  // "Assigned User Groups" is derived from the user-group list (each group
  // assigns a role per company). Fetched quietly and only for users who can
  // view user groups -- the column simply stays empty otherwise.
  const [userGroups, setUserGroups] = useState([]);
  useEffect(() => {
    if (!provisioningLoaded || !hasViewAccess || !canViewUserGroups || !token) return undefined;
    let cancelled = false;
    fetch(`${domain}user-group/details/`, {
      headers: { "content-type": "application/json", Authorization: `token ${token}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setUserGroups(Array.isArray(data) ? data : data?.results ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canViewUserGroups, domain, hasViewAccess, provisioningLoaded, token]);

  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [viewRole, setViewRole] = useState(null);
  const [viewRefreshKey, setViewRefreshKey] = useState(0);
  // The full-page Loader is for the first load only; refetches after a
  // save or delete keep the current rows visible (2026-10-01, user request).
  const [loadedOnce, setLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loading) setLoadedOnce(true);
  }, [loading]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // null until CustomFilter reports its first filtered set, so the table
  // shows the fetched rows immediately instead of flashing "no results".
  const [toolbarRows, setToolbarRows] = useState(null);
  const tableContainerRef = useRef(null);
  const [filterBarRef, filterBarHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();

  const { titleOverride, visibleHeaders, subModuleId } = useListToolbar({
    headers: ROLE_HEADERS,
    defaultViewId: ROLE_LIST_DEFAULT_VIEW_ID,
    storageKey: "role_saved_views",
    domain,
    userDetails,
    userScreenAccess,
    subModuleName: "Roles",
    screenAccess: hasViewAccess,
    onViewChange: () => setPage(0),
    legacyHeaders: LEGACY_ROLE_HEADERS,
  });
  const totalWeight = visibleHeaders.reduce(
    (sum, header) => sum + (COLUMN_WEIGHTS[header.key] ?? 15),
    0,
  );
  const columnWidth = (key) =>
    `${(((COLUMN_WEIGHTS[key] ?? 15) / totalWeight) * 100).toFixed(2)}%`;

  const rows = useMemo(
    () =>
      (Array.isArray(roles) ? roles : []).map((role) => {
        const groups = getGroupsForRole(userGroups, role);
        const status = getRoleStatus(role);
        return {
          ...role,
          productList: role?.products ?? [],
          products: (role?.products ?? []).join(", "),
          groups,
          user_groups: groups.map((group) => group?.name).filter(Boolean).join(", "),
          statusLabel: status,
          status_label: status,
        };
      }),
    [roles, userGroups],
  );

  // The stat cards filter by status first; CustomFilter's search, filters
  // and sort then apply on top of that.
  const cardRows = useMemo(
    () => (statusFilter ? rows.filter((role) => role.statusLabel === statusFilter) : rows),
    [rows, statusFilter],
  );
  const filteredRows = toolbarRows ?? cardRows;

  useEffect(() => {
    setPage(0);
  }, [toolbarRows, statusFilter]);

  useEffect(() => {
    scrollTop(tableContainerRef);
  }, [page]);

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((role) => role.statusLabel === "Active").length;
    return { total, active, inactive: total - active };
  }, [rows]);

  useEffect(() => {
    if (!editSavedSignal) return;
    setLoad(true);
    setViewRefreshKey((key) => key + 1);
    // setLoad is a stable state setter from useFetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSavedSignal]);

  // Keep the open View Role panel on the refetched row (new name/status).
  useEffect(() => {
    if (!viewRole) return;
    const fresh = rows.find((row) => String(row?.role_id) === String(viewRole?.role_id));
    if (fresh && fresh !== viewRole) setViewRole(fresh);
    // Only re-sync when the rows change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const openRole = (role) => {
    dispatch(setGlobalSearchValue(""));
    setViewRole(role);
  };

  // Opened from a User Group's "Related User Roles" row. The request only
  // carries the role's id/name (this list may still be loading when the
  // tab switches); the row-sync effect above swaps in the full row -- with
  // its description and status -- once the list has it.
  useEffect(() => {
    const role = openRoleRequest?.role;
    if (!role?.role_id) return;
    const fresh = rows.find((row) => String(row?.role_id) === String(role.role_id));
    openRole(fresh ?? role);
    // Only on a new request; rows are picked up by the sync effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRoleRequest]);

  const editRole = (role, options = {}) => {
    dispatch(setGlobalSearchValue(""));
    onEditExpandedChange?.(Boolean(options.expanded));
    // viewRole is kept (the panel is only hidden while Edit is open), so
    // closing Edit (outside click, Close or Cancel) returns to the role's
    // view instead of dropping both. A successful save remounts the list.
    setEditRowDetails(role);
    setScreenNavigation("Edit Role");
  };

  const deleteRole = async (role) => {
    try {
      const response = await fetch(`${domain}product-control/roles/delete/?role_id=${role?.role_id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `token ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showToast("success", "Role deleted.");
        setViewRole(null);
        setLoad(true);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        showToast("error", data?.error || data?.detail || data?.message || "Couldn't delete the role. Please try again.");
      }
    } catch (error) {
      showToast("error", "Couldn't delete the role. Please try again.");
    }
  };

  if (!provisioningLoaded || (loading && !loadedOnce)) return <Loader info="Loading..." />;
  if (!hasViewAccess) return <Loader info="Redirecting..." />;

  const statCards = [
    {
      key: "",
      title: "Total Roles",
      value: stats.total,
      detail: `${pct(stats.total, stats.total)}% of all roles`,
      icon: GroupsRounded,
      accent: "#1D4ED8",
      tint: "#EEF4FF",
    },
    {
      key: "Active",
      title: "Active Roles",
      value: stats.active,
      detail: `${pct(stats.active, stats.total)}% of all roles`,
      icon: CheckCircleRounded,
      accent: "#0F766E",
      tint: designColors.tealTint,
    },
    {
      key: "Inactive",
      title: "Inactive Roles",
      value: stats.inactive,
      detail: `${pct(stats.inactive, stats.total)}% of all roles`,
      icon: PeopleOutlineRounded,
      accent: "#64748B",
      tint: "#F1F5F9",
    },
  ];

  const pagedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderCell = (key, role) => {
    switch (key) {
      case "role_name":
        return (
          <Mui.Typography sx={{ fontSize: 14.5, fontWeight: 700, color: designColors.slate800, wordBreak: "break-word" }}>
            {role?.role_name}
          </Mui.Typography>
        );
      case "description":
        return (
          <Mui.Typography
            title={role?.description || ""}
            sx={{
              fontSize: 14,
              color: role?.description ? designColors.slate700 : designColors.slate400,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {role?.description || "-"}
          </Mui.Typography>
        );
      case "products":
        return <ChipList items={role.productList} max={3} />;
      case "user_groups":
        return (
          <ChipList
            items={role.groups.map((group) => group?.name).filter(Boolean)}
            max={2}
            emptyLabel={canViewUserGroups ? "Not assigned" : "-"}
          />
        );
      case "status_label":
        return statusChip(role.statusLabel);
      default:
        return null;
    }
  };

  return (
    <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
    <Mui.Box sx={{ width: "100%" }}>
      <Mui.Grid container spacing={2} sx={{ mb: 2 }}>
        {statCards.map((card) => (
          <Mui.Grid item xs={12} sm={4} key={card.title}>
            <StatCard
              icon={card.icon}
              title={card.title}
              value={card.value}
              detail={card.detail}
              accent={card.accent}
              tint={card.tint}
              isActive={statusFilter === card.key}
              onClick={() => setStatusFilter(card.key)}
            />
          </Mui.Grid>
        ))}
      </Mui.Grid>

      <Mui.Box
        ref={filterBarRef}
        sx={{ ...roleAndGroupUi.sectionCard, ...roleAndGroupUi.stickyCustomFilterBar, mb: 2 }}
      >
        <CustomFilter
          title="roles"
          titleOverride={titleOverride}
          filterHeaders={visibleHeaders}
          data={cardRows}
          filteredData={filteredRows}
          filteredDataReady={toolbarRows !== null}
          onFilteredDataChange={setToolbarRows}
          screenAccess={hasViewAccess}
          hideCount={true}
          labeledToolbar={true}
          downloadFormats={["excel", "pdf", "csv"]}
          searchPlaceholder="Search roles..."
        />
      </Mui.Box>

      <Mui.Box sx={{ ...roleAndGroupUi.sectionCard, overflow: "clip" }}>
        <Mui.TableContainer
          ref={tableContainerRef}
          sx={{
            width: "100%",
            overflowX: { xs: "auto", lg: "visible" },
            pb: footerHeight ? `${footerHeight}px` : 0,
          }}
        >
          <ApproveTable stickyHeader sx={{ width: "100%", minWidth: 960, tableLayout: "fixed" }}>
            <Mui.TableHead sx={designShell.tableStickyHead}>
              <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                {visibleHeaders.map((header) => (
                  <Mui.TableCell
                    key={header.key}
                    sx={{
                      ...roleAndGroupUi.tableHeadCell,
                      top: APP_BAR_OFFSET + filterBarHeight,
                      width: columnWidth(header.key),
                      py: 1.5,
                      fontWeight: 700,
                    }}
                  >
                    {header.name}
                  </Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {filteredRows.length === 0 ? (
                <TableEmptyState dataLength={rows} colSpan={visibleHeaders.length} />
              ) : (
                pagedRows.map((role) => (
                  <Mui.TableRow
                    key={role?.role_id}
                    hover
                    selected={viewRole?.role_id === role?.role_id}
                    onClick={() => openRole(role)}
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "#ECFAF6" },
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    {visibleHeaders.map((header) => (
                      <Mui.TableCell
                        key={header.key}
                        data-label={header.key === "role_name" ? "" : undefined}
                        sx={{ ...roleAndGroupUi.tableBodyCell, verticalAlign: "middle" }}
                      >
                        {renderCell(header.key, role)}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </ApproveTable>
        </Mui.TableContainer>
        <Mui.Box ref={footerRef} sx={roleAndGroupUi.stickyFooterBar}>
          {paginationFooter({
            page,
            rowsPerPage,
            total: filteredRows.length,
            itemLabel: "roles",
            onPageChange: setPage,
            onRowsPerPageChange: (event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            },
          })}
        </Mui.Box>
      </Mui.Box>

      {/* Hidden (not closed) while Edit is open: the view drawer sits above
          the expanded Edit full view, so it would cover it. Staying "open"
          keeps its loaded data, so it comes back without a refetch/loader
          when Edit closes without saving. */}
      <RoleViewPanel
        open={Boolean(viewRole)}
        hidden={isEditOpen}
        refreshKey={viewRefreshKey}
        restoreExpanded={editExpanded}
        role={viewRole}
        onClose={() => setViewRole(null)}
        canEdit={canEdit}
        onEdit={editRole}
        onDelete={(role) => setDeleteTarget(role)}
        userGroups={viewRole ? getGroupsForRole(userGroups, viewRole) : []}
        onOpenUserGroup={
          canViewUserGroups && onOpenUserGroup
            ? (group) => {
                setViewRole(null);
                onOpenUserGroup(group);
              }
            : undefined
        }
      />

      <GlobalConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete the role"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          const target = deleteTarget;
          setDeleteTarget(null);
          deleteRole(target);
        }}
        confirmLabel="Delete"
        danger
      >
        Do you want to delete the role "{deleteTarget?.role_name}"?
      </GlobalConfirmDialog>
    </Mui.Box>
    </FilterProvider>
  );
};
